import { useI18n } from "@baton/i18n";
import { X } from "@baton/icons";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../cn.ts";
import { IconButton } from "../../primitives/buttons/IconButton/IconButton.tsx";
import { useScrollFade } from "../../primitives/layout/ScrollArea/scrollFade.ts";
import { sheetBodyClassName, sheetHeaderClassName, sheetPanelClassName, sheetTitleClassName } from "../surfaces.ts";

/**
 * FR-015-040, FR-015-110, § Overlays, SidePeek: custom, drawn with shadcn/ui's Sheet parts
 * (2026-09-15, by the owner's decision), because resizing and the presentation switcher exist in
 * neither source and the layout behind it stays usable. It is `role="complementary"` with an
 * `aria-label` naming the item rather than a dialog (§ Names, roles and structure); resizable
 * between 380 px and 720 px with the width persisted by the host; sliding in from the inline end
 * on the slow tier; and the presentation switcher slot. `Esc` closes it and the host restores
 * focus to the originating row (§ Keyboard), unless a control inside took the key first: an inline
 * edit giving up its draft, or a picker or menu closing, each of which prevents the event's default,
 * and the menus and pickers from their portals, whose keys bubble through React into the peek
 * (2026-10-10; until then one `Esc` cancelled the edit and closed the peek together). The title row draws the host's `title` where it has one
 * (the task peek's project and identifier, once, 2026-10-03) and its `actions` before the close
 * button (the task peek's previous and next), and the host's `onKeyDown` sees every key pressed in
 * the panel but `Esc`.
 */
export const PEEK_MIN_WIDTH = 380;
export const PEEK_MAX_WIDTH = 720;
/** The width a peek opens at until the reader resizes one (2026-10-03, by the owner's approval of the product review: "a side peek about 560 px wide with the list visible"). */
export const PEEK_DEFAULT_WIDTH = 560;
export const PEEK_KEYBOARD_STEP = 16;

export interface SidePeekProps {
  /** The item the peek shows, which is also its accessible name. */
  readonly itemLabel: string;
  /** What the title row draws for the item, the `itemLabel` when absent. */
  readonly title?: ReactNode;
  /** Controls at the end of the title row, before the close button. */
  readonly actions?: ReactNode;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  readonly width: number;
  readonly onWidthChange: (width: number) => void;
  readonly onClose: () => void;
  /** The presentation switcher (side peek, modal, full page) the item detail renders. */
  readonly presentationSwitcher?: ReactNode;
  /** The width the handle keeps the panel between, the peek's 380 to 720 px when absent; 019's assistant panel docks in the same frame between 360 and 560 px (`contracts/design-system.md` § Assistant panel, 2026-10-15). */
  readonly bounds?: PeekBounds;
  readonly children: ReactNode;
  readonly className?: string;
}

export interface PeekBounds {
  readonly min: number;
  readonly max: number;
}

export const PEEK_BOUNDS: PeekBounds = { min: PEEK_MIN_WIDTH, max: PEEK_MAX_WIDTH };

export function clampPeekWidth(width: number, bounds: PeekBounds = PEEK_BOUNDS): number {
  return Math.min(bounds.max, Math.max(bounds.min, Math.round(width)));
}

export function SidePeek({ itemLabel, title, actions, onKeyDown, width, onWidthChange, onClose, presentationSwitcher, bounds = PEEK_BOUNDS, children, className }: SidePeekProps) {
  const { t } = useI18n();
  // The body fades at an edge its content continues past (`scrollFade.ts`, 2026-10-21).
  const body = useScrollFade<HTMLDivElement>();
  const dragging = useRef<{ startX: number; startWidth: number } | null>(null);
  const widthChange = useRef(onWidthChange);
  const boundsRef = useRef(bounds);
  useEffect(() => {
    widthChange.current = onWidthChange;
    boundsRef.current = bounds;
  }, [onWidthChange, bounds]);

  // The two window listeners of a drag, created once so they can remove each other.
  const [handlers] = useState(() => {
    const move = (event: PointerEvent) => {
      const drag = dragging.current;
      if (!drag) return;
      const rtl = getComputedStyle(document.documentElement).direction === "rtl";
      const delta = (drag.startX - event.clientX) * (rtl ? -1 : 1);
      widthChange.current(clampPeekWidth(drag.startWidth + delta, boundsRef.current));
    };
    const stop = () => {
      dragging.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    return { move, stop };
  });
  const stopDrag = useCallback(() => {
    handlers.stop();
  }, [handlers]);
  useEffect(() => stopDrag, [stopDrag]);

  return (
    <aside
      role="complementary"
      aria-label={itemLabel}
      data-slot="side-peek"
      className={cn(
        sheetPanelClassName,
        "relative flex-none min-w-(--peek-width-min) max-w-(--peek-width-max) border-s animate-in duration-(--duration-slow) slide-in-from-end max-md:fixed max-md:inset-0 max-md:z-(--layer-peek) max-md:w-full! max-md:min-w-0 max-md:max-w-none",
        className,
      )}
      style={{ inlineSize: `${clampPeekWidth(width, bounds)}px`, minInlineSize: `${bounds.min}px`, maxInlineSize: `${bounds.max}px` }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          if (event.defaultPrevented) return;
          event.stopPropagation();
          onClose();
          return;
        }
        onKeyDown?.(event);
      }}
    >
      <button
        type="button"
        data-slot="side-peek-handle"
        className="absolute inset-y-0 -start-(--space-2) w-(--space-3) cursor-col-resize border-0 bg-transparent p-0 hover:bg-accent-bg focus-visible:bg-accent-bg"
        aria-label={t("ui.peek.resize")}
        aria-valuemin={bounds.min}
        aria-valuemax={bounds.max}
        aria-valuenow={clampPeekWidth(width, bounds)}
        role="separator"
        aria-orientation="vertical"
        onPointerDown={(event) => {
          dragging.current = { startX: event.clientX, startWidth: width };
          window.addEventListener("pointermove", handlers.move);
          window.addEventListener("pointerup", handlers.stop);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") onWidthChange(clampPeekWidth(width + PEEK_KEYBOARD_STEP, bounds));
          if (event.key === "ArrowRight") onWidthChange(clampPeekWidth(width - PEEK_KEYBOARD_STEP, bounds));
          if (event.key === "Home") onWidthChange(bounds.max);
          if (event.key === "End") onWidthChange(bounds.min);
        }}
      />
      <div data-slot="sheet-header" className={sheetHeaderClassName}>
        {presentationSwitcher}
        <span data-slot="sheet-title" className={sheetTitleClassName}>
          {title ?? itemLabel}
        </span>
        {actions !== undefined && <span className="inline-flex flex-none items-center gap-(--space-1)">{actions}</span>}
        <IconButton icon={<X />} label={t("ui.dialog.close")} size="sm" onClick={onClose} />
      </div>
      <div ref={body} data-slot="sheet-body" className={sheetBodyClassName}>
        {children}
      </div>
    </aside>
  );
}
